import { useCallback, useEffect, useRef } from "react";
import { InformacaoFaturamento } from "../../../../domain/models/informacao_faturamento";
import { InformacaoFaturamentoForm } from "../../../schemas/CentroCusto_cadastros/informacaoFaturamento";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { FormCPFMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_cpf_masked_input";
import { FormPhoneMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_phone_masked_input";
import { ClienteDropDown } from "../../../../../../common/domain/models/cliente_dropdown";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { UseFormReturn, useWatch } from "react-hook-form";
import { FormCNPJMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_cnpj_masked_input";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { Cep } from "../../../../../rh/domain/models/cep";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../../../common/hooks/use_debounced_callback";
import { Cliente } from "../../../../domain/models/cliente";
import { DropDownClienteEmpresa } from "../../../../../../common/components/dropdown_cliente_empresa";

type Props = {
    open: boolean,
    saving: boolean,
    selectedInformacaoFaturamento: InformacaoFaturamento | undefined,
    listDropDownClientes: ClienteDropDown[],
    hookForm: UseFormReturn<InformacaoFaturamentoForm>,
    onSubmit: (form: InformacaoFaturamentoForm) => void,
    onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
    onClose: (idInformacaoFaturamento: number | null, openState: boolean) => void,
    onLoadCep: (cep: string) => Promise<Cep | undefined>,
    onGetClienteById: (idCliente: number) => Promise<Cliente | undefined>


}


export const CadastroInformacoesFaturamentoModal = ({
    open,
    saving,
    selectedInformacaoFaturamento,
    hookForm,
    onLoadCep,
    onSubmit,
    onClose,
    onOpenChangeHistory,
    onGetClienteById,

}: Props) => {
    const onLoadCepSkiper = useRef(false);
    const debouncedReff = useCreateDebounceHandler(750);

    const idCliente = useWatch({
        control: hookForm.control,
        name: "idCliente"
    });

    const loadCep = useDebouncedCallback(debouncedReff, async (cep: string) => {
        return async () => {
            if (typeof cep === "string") {
                const normalizedCep = cep.trim();
                const requiredSize = 'xx.xxx-xxx';

                if (normalizedCep.length === requiredSize.length) {
                    const cepInfo = await onLoadCep(cep);
                    if (cepInfo) {
                        hookForm.setValue('rua', cepInfo.logradouro.toUpperCase());
                        hookForm.setValue('bairro', cepInfo.bairro.toUpperCase());
                        hookForm.setValue('cidade', cepInfo.cidade.toUpperCase());
                        hookForm.setValue('uf', cepInfo.uf.toUpperCase());
                        hookForm.setValue('pais', cepInfo.pais.toUpperCase());
                    }
                }
                else if (normalizedCep.length === 0) {
                    hookForm.setValue('rua', '');
                    hookForm.setValue('bairro', '');
                    hookForm.setValue('cidade', '');
                    hookForm.setValue('uf', '');
                    hookForm.setValue('pais', '');
                }
            }
        };
    }, [hookForm, onLoadCep]);

    const loadClienteById = useCallback(async (idCliente: number) => {
        try {
            const cliente = await onGetClienteById(idCliente);

            hookForm.setValue("nomeFantasia", cliente?.nomeFantasia ?? "")
            hookForm.setValue("razaoSocial", cliente?.razaoSocial ?? "")
            hookForm.setValue("cpf", cliente?.cpf ?? "")
            hookForm.setValue("cnpj", cliente?.cnpj ?? "")
            hookForm.setValue("contato", cliente?.contato ?? "")
            hookForm.setValue("email", cliente?.email ?? "")
            hookForm.setValue("inscricaoEstadual", cliente?.incricaoEstadual ?? "")
            hookForm.setValue("inscricaoMunicipal", cliente?.incricaoMunicipal ?? "")
            hookForm.setValue("telefoneFixo", cliente?.telefoneFixo ?? "")
            hookForm.setValue("telefoneCelular", cliente?.telefoneCelular ?? "")

        } catch (error) {

        }
    }, [hookForm,
        onGetClienteById])


    useEffect(() => {
        if (selectedInformacaoFaturamento) {
            onLoadCepSkiper.current = true;
        }
        if (!selectedInformacaoFaturamento && idCliente) {
            loadClienteById(idCliente)
        }
    }, [selectedInformacaoFaturamento,
        idCliente,
        loadClienteById]);


    const handleChangeHistory = useCallback(() => {
        if (selectedInformacaoFaturamento) {
            onOpenChangeHistory(selectedInformacaoFaturamento.idInformacaoFaturamento, LogTipoTabela.INFORMACAO_FATURAMENTO);
        }
    }, [selectedInformacaoFaturamento, onOpenChangeHistory]);

    const handleClose = useCallback(() => {
        if (!saving) onClose(null, false);
    }, [
        saving,
        onClose,
    ]);

    const handleSubmit = useCallback((form: InformacaoFaturamentoForm) => {
        if (!saving) onSubmit(form);
    }, [
        saving,
        onSubmit,
    ]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            slotProps={{
                paper: {
                    component: 'form',
                    onSubmit: hookForm.handleSubmit(handleSubmit)
                }
            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "100%",
                    },
                },
            }}
        >

            <DialogTitle>
                {selectedInformacaoFaturamento ? "Editar" : "Cadastrar"} Informações de Faturamento
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}>
                    <FormContainer title={"Informações"} arrangement="fullRow">
                        <>
                            <div className="flex flex-row w-full gap-5">
                                <div className="w-[60%]">
                                    <DropDownClienteEmpresa
                                        name='idCliente'
                                        label='Dados Faturamento'
                                        required={false}
                                        reactHookForm={hookForm}
                                    />

                                </div>
                                <div className="w-full flex flex-row justify-center items-center">
                                    <FormRadioGroupInput
                                        name='principal'
                                        label='Faturamento Principal'
                                        options={[
                                            { id: 0, label: 'Não' },
                                            { id: 1, label: 'Sim' },
                                        ]}
                                        reactHookForm={hookForm} />
                                </div>


                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='nomeFantasia'
                                    label='Nome Fantasia'
                                    required={true}
                                    reactHookForm={hookForm} />

                                <FormCNPJMaskedInput
                                    name='cnpj'
                                    label='CNPJ'
                                    required={false}
                                    reactHookForm={hookForm} />

                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='razaoSocial'
                                    label='Razão Social'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <FormCPFMaskedInput
                                    name='cpf'
                                    label='CPF'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='contato'
                                    label='Contato'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <DefaultFormInput
                                    name='email'
                                    label='Email'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='inscricaoEstadual'
                                    label='Inscrição Estadual'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <FormPhoneMaskedInput
                                    name='telefoneFixo'
                                    label='Tel Fixo'
                                    type="fixo"
                                    reactHookForm={hookForm} />

                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='inscricaoMunicipal'
                                    label='Inscrição Municipal'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <FormPhoneMaskedInput
                                    name='telefoneCelular'
                                    label='Tel Celular'
                                    type="movel"
                                    reactHookForm={hookForm} />

                            </div>

                            <h3 className="font-bold text-black dark:text-white text-2xl">Endereço de Faturamento</h3>
                            <hr />

                            <div className="flex flex-row gap-3">
                                <FormMaskedInput
                                    name='cep'
                                    label={"CEP"}
                                    unmask={false}
                                    mask="00.000-000"
                                    maskPlaceholder="__.___-___"
                                    reactHookForm={hookForm}
                                    onChange={(cep) => {
                                        const normalizedCep = cep.trim().replaceAll('_', '');
                                        const requiredSize = 'xx.xxx-xxx';

                                        if (!onLoadCepSkiper.current) {
                                            if (normalizedCep.length === 0 || normalizedCep.length === requiredSize.length) {
                                                loadCep(cep);
                                            }
                                        }
                                        onLoadCepSkiper.current = false;
                                    }} />


                                <DefaultFormInput
                                    name='cidade'
                                    label='Cidade'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='rua'
                                    label='Rua/Bairro/Alameda'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <DefaultFormInput
                                    name='numero'
                                    label='Numero'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='apartamento'
                                    label='Complemento'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <DefaultFormInput
                                    name='bairro'
                                    label='Bairro'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='uf'
                                    label='Estado'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <DefaultFormInput
                                    name='pais'
                                    label='País'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>
                        </>

                    </FormContainer>


                </ModalLoading>
            </DialogContent>
            <DialogActions>
                <div className="w-full h-full items-center justify-between flex p-4 gap-4">
                    <div>
                        {
                            isNotNullOrUndefined(selectedInformacaoFaturamento) &&
                            <PrimaryButton
                                buttonColor="yellow"
                                onClick={handleChangeHistory}>
                                <span className="text-xs">
                                    Historico de alterações
                                </span>
                            </PrimaryButton>
                        }
                    </div>

                    <div className="flex flex-row gap-2">
                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Salvar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>
        </Dialog>
    )
}